/** Display labels for the closed vocabularies in shared/model.ts. */
import type {
  AVAILABILITY,
  BOOK_FORMATS,
  BOOK_RELATIONS,
  CHARACTER_STYLES,
  COMPLEXITIES,
  GAME_SIZES,
  GAME_TYPES,
  PLAY_MODES,
  REPORT_REASONS,
  SERIES_KINDS,
  SUPPORT_CATEGORIES,
  SYSTEM_RELATIONS,
  SYSTEM_STATUSES,
} from '../../shared/model'

type Labels<T extends readonly string[]> = Record<T[number], string>

export const STATUS_LABELS: Labels<typeof SYSTEM_STATUSES> = {
  '': 'Unknown',
  active: 'Active',
  'in-development': 'In development',
  playtest: 'Playtest',
  discontinued: 'Discontinued',
  legacy: 'Legacy',
}

export const COMPLEXITY_LABELS: Labels<typeof COMPLEXITIES> = {
  '': 'Unknown',
  'rules-light': 'Rules-light',
  'rules-medium': 'Medium',
  'rules-heavy': 'Rules-heavy',
}

export const CHARACTER_STYLE_LABELS: Labels<typeof CHARACTER_STYLES> = {
  'class-based': 'Class-based',
  'skill-based': 'Skill-based',
  'level-based': 'Level-based',
  classless: 'Classless',
  'point-buy': 'Point-buy',
  'playbook-based': 'Playbooks',
}

export const PLAY_MODE_LABELS: Labels<typeof PLAY_MODES> = {
  'gm-required': 'Needs a GM',
  'gm-optional': 'GM optional',
  'gm-less': 'GM-less',
  solo: 'Solo',
  'co-op': 'Co-op',
}

export const SYSTEM_RELATION_LABELS: Labels<typeof SYSTEM_RELATIONS> = {
  predecessor: 'Earlier edition',
  successor: 'Later edition',
  'derived-from': 'Derived from',
  'inspired-by': 'Inspired by',
  'compatible-with': 'Compatible with',
  related: 'Related',
}
/** How a relation reads from the other end ("Gradient Descent requires PSG" -> "Required by"). */
export const SYSTEM_RELATION_INVERSE: Labels<typeof SYSTEM_RELATIONS> = {
  predecessor: 'Later edition',
  successor: 'Earlier edition',
  'derived-from': 'Basis for',
  'inspired-by': 'Inspired',
  'compatible-with': 'Compatible with',
  related: 'Related',
}

export const BOOK_RELATION_LABELS: Labels<typeof BOOK_RELATIONS> = {
  requires: 'Requires',
  expands: 'Expands',
  'compatible-with': 'Compatible with',
  'revision-of': 'Revision of',
  'remaster-of': 'Remaster of',
  'translation-of': 'Translation of',
  collects: 'Collects',
  related: 'Related',
}
export const BOOK_RELATION_INVERSE: Labels<typeof BOOK_RELATIONS> = {
  requires: 'Required by',
  expands: 'Expanded by',
  'compatible-with': 'Compatible with',
  'revision-of': 'Revised as',
  'remaster-of': 'Remastered as',
  'translation-of': 'Translated as',
  collects: 'Collected in',
  related: 'Related',
}

export const FORMAT_LABELS: Labels<typeof BOOK_FORMATS> = {
  pdf: 'PDF',
  epub: 'EPUB',
  hardcover: 'Hardcover',
  softcover: 'Softcover',
  'boxed-set': 'Boxed set',
  zine: 'Zine',
  'print-on-demand': 'Print on demand',
  'vtt-module': 'VTT module',
  zip: 'Download pack',
}

export const AVAILABILITY_LABELS: Labels<typeof AVAILABILITY> = {
  '': 'Unknown',
  free: 'Free',
  'pay-what-you-want': 'Pay what you want',
  paid: 'Paid',
  'out-of-print': 'Out of print',
  unavailable: 'No longer available',
}

export const SERIES_KIND_LABELS: Labels<typeof SERIES_KINDS> = {
  setting: 'Setting',
  series: 'Series',
  'product-line': 'Product line',
  collection: 'Collection',
  campaign: 'Campaign',
}

export const GAME_TYPE_LABELS: Labels<typeof GAME_TYPES> = {
  ttrpg: 'Tabletop RPG',
  wargame: 'Wargame',
}

export const GAME_SIZE_LABELS: Labels<typeof GAME_SIZES> = {
  skirmish: 'Skirmish',
  squad: 'Squad',
  battle: 'Battle',
  'mass-battle': 'Mass battle',
}

export const REPORT_REASON_LABELS: Labels<typeof REPORT_REASONS> = {
  incorrect: 'Wrong information',
  duplicate: 'Duplicate of another record',
  spam: 'Spam or vandalism',
  copyright: 'Copyright problem (e.g. a pirated file linked)',
  offensive: 'Offensive content',
  other: 'Something else',
}

export const SUPPORT_CATEGORY_LABELS: Labels<typeof SUPPORT_CATEGORIES> = {
  bug: 'Something isn’t working',
  feature: 'A feature idea',
  feedback: 'Feedback',
  account: 'Account or sign-in problem',
  privacy: 'Privacy, my data or deleting my account',
  other: 'Something else',
}

export const TRUST_LABELS: Record<string, string> = {
  new: 'New contributor',
  contributor: 'Contributor',
  trusted: 'Trusted contributor',
  editor: 'Editor',
  moderator: 'Moderator',
  admin: 'Admin',
}

/** A label from one of the maps above, falling back to the raw value. */
export const label = <T extends string>(map: Record<T, string>, v: string) => map[v as T] ?? v
